import { AppImageDisplayInfo } from "client/Api/AppIcon/common";
import { AppColorViewModel, AppType } from "client/Api/Apps/common";
import {
	GridArea,
	GridDimension,
	GridPosition
} from "client/Api/Workspace/common";
import {
	ClientTileModel,
	ClientTileModelBase,
	ConditionalAccessDisabledTileGroup,
	SpaceAccessKind,
	SpaceKind,
	TileGroupAccessKind,
	TileGroupType,
	TileGroupWidth
} from "client/Api/Workspace/workspace";
import { InteractionState } from "../Tiles/TileContext";
import { IPackingAlgorithm } from "client_legacy/Workspace/Packing/common";
import { DatabaseOrTempObjectId } from "client_legacy/ndaw/tempOrDatabaseId";

export type WorkspaceInitializationModel = {
	groups: readonly TileGroupInitializationModel[];
};

export type TileGroupInitializationModel = {
	data: TileGroupData;
	tiles: readonly TileData[];
};

export const PLACEHOLDER_ID = Symbol();
export type PlaceHolderId = typeof PLACEHOLDER_ID;

export const groupWidthToTileGroupColumnSpan: Record<TileGroupWidth, number> = {
	[TileGroupWidth.Regular]: 1,
	[TileGroupWidth.Wide]: 2
};

export const widestTileGroupColumnSpan: number = groupWidthToTileGroupColumnSpan[TileGroupWidth.Wide];

export type TileGroupColumnSpanInTileGroupGrid = {
	value: number;
	kind: "normal" | "reduced";
};

export type DimensionLimitations = {
	minColumns: number;
	maxColumns: number;
	minRows: number;
	maxRows: number;
};

export const defaultTileGroupGridDimensionLimits: DimensionLimitations = {
	maxColumns: widestTileGroupColumnSpan,
	minColumns: widestTileGroupColumnSpan,
	minRows: 1,
	maxRows: Number.MAX_VALUE
};

export type TileGridDimensionLimits = {
	minRows: number;
	columns: number;
};

export type TileGridConfiguration = {
	dimensionLimits: TileGridDimensionLimits;
	renderLiveTilesFullWidth: boolean;
};

export const defaultTileGridConfiguration: TileGridConfiguration = {
	dimensionLimits: {
		columns: 8,
		minRows: 1
	},
	renderLiveTilesFullWidth: false
};

export type TileGroupPlaceHolderRenderState = {
	data: { id: typeof PLACEHOLDER_ID; };
	tileGroupId: DatabaseOrTempObjectId;
	position: GridPosition;
	dimension: GridDimension;
};

export type SpaceOptions = {
	id: number;
	name: string;
	kind: SpaceKind;
	accessKind: SpaceAccessKind;
	canRearrangeTileGroupsInSpace: boolean;
};

export type SpaceRenderState = {
	// The groups in this array are in the DOM render order
	groups: readonly TileGroupRenderState[];
	placeholder: TileGroupPlaceHolderRenderState | undefined;
	options: OptionalModel<SpaceOptions>;
	tileGroupGridDimensionLimits: DimensionLimitations;
	regularGroupWidthTileGridConfiguration: TileGridConfiguration;
	isInEditMode: boolean;
	automaticallyReorderTilesForNoneEditableTileGroups: boolean;
	dimension: GridDimension;
};

export type TileConditionalAccess = {
	reason: string;
	notificationMessage: string;
	hideTile: boolean;
};

export type TileMaintenanceWindow = {
	endsOn: Date;
	notificationMessage: string;
};

export type AppTypeSpecificTileSettings<TKind extends AppType> = Omit<Extract<OmitDistributive<ClientTileModel, keyof ClientTileModelBase>, { type: TKind; }>, "type">;
export type AppTypeAndTileSettings = { [k in AppType]: { readonly type: k; readonly settings: AppTypeSpecificTileSettings<k>; } }[AppType];

export type TileData<TType extends AppType = AppType> = Extract<AppTypeAndTileSettings, { type: TType; }> & {
	readonly id: number;
	readonly environmentAppId: number;
	readonly name: string;
	readonly icon: AppImageDisplayInfo;
	readonly color: AppColorViewModel;
	readonly position: GridPosition;
	readonly dimension: GridDimension;
	readonly isConfiguredCorrectly: boolean;
	readonly appIsNotAccessible: boolean;
	readonly appIsLicensed: boolean;
	readonly conditionalAccess?: TileConditionalAccess | nil;
	readonly maintenanceWindow?: TileMaintenanceWindow | nil;
	readonly shouldHideName: OptionalModel<boolean>;
};

type NormalDimensionToRender = {
	kind: "normal";
	dimension: GridDimension;
};

type ResponsiveDimensionToRender = {
	kind: "responsive";
	dimension: GridDimension;
};

export type DimensionToRender =
	| NormalDimensionToRender
	| ResponsiveDimensionToRender;

export type TileRenderState<TType extends AppType = AppType> = {
	readonly data: TileData<TType>;
	readonly isBeingDragged: boolean;
	readonly isVisible: boolean;
	readonly interactionState: InteractionState;
	readonly position: GridPosition;
	readonly dimension: DimensionToRender;
};

export type TilePlaceHolderRenderState = {
	data: { id: typeof PLACEHOLDER_ID; };
	tileId: number;
	position: GridPosition;
	dimension: GridDimension;
};

export enum PackingAlgorithmKind {
	Top = 1,
	LargeToSmallTopLeftFill = 2
}

export type TileGridRenderSettings = {
	configuredPackingAlgorithm: PackingAlgorithmKind;
	configuration: TileGridConfiguration;
	isWidthReduced: boolean;
	packingAlgorithm: IPackingAlgorithm;
};

export type TileGridRenderState = {
	// The tiles in this array are in the DOM render order
	tiles: readonly TileRenderState[];
	settings: TileGridRenderSettings;
	dimension: GridDimension;
	placeholder: TilePlaceHolderRenderState | undefined;
};

export type TileGroupData = {
	id: DatabaseOrTempObjectId;
	kind: TileGroupType;
	width: TileGroupWidth;
	name: string;
	accessKind: TileGroupAccessKind;
	isHiddenByDefaultOnPersonalSpace: boolean;
	conditionalAccess?: ConditionalAccessDisabledTileGroup | nil;
};

export type TileGroupVisibilityPreference = OptionalModel<"visible" | "hidden">;

export type TileGroupRenderState = {
	data: TileGroupData;
	visibilityPreference: TileGroupVisibilityPreference;
	order: number;
	isVisible: boolean;
	position: GridPosition;
	dimension: GridDimension;
	tileGrid: TileGridRenderState;
};

export function arePositionsEqual(lhs: GridPosition, rhs: GridPosition): boolean {
	return lhs.column === rhs.column
		&& lhs.row === rhs.row;
}

export function areDimensionsEqual(lhs: GridDimension, rhs: GridDimension): boolean {
	return lhs.columnSpan === rhs.columnSpan
		&& lhs.rowSpan === rhs.rowSpan;
}

export function areAreasEqual(lhs: GridArea, rhs: GridArea): boolean {
	return arePositionsEqual(lhs.position, rhs.position)
		&& areDimensionsEqual(lhs.dimension, rhs.dimension);
}